import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  HostListener,
  ViewChild,
  ElementRef
} from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { Reminder, ReminderStats, ReminderType } from '../models/reminder.model';
import { RemindersService } from '../services/reminders.service';
import { getTypeIcon, getTypeColor, getTypeBgColor } from '../utils/icon-mapping.util';
import { AddReminderModalComponent } from './add-reminder-modal/add-reminder-modal.component';

@Component({
    selector: 'app-reminders-panel',
    templateUrl: './reminders-panel.component.html',
    styleUrls: ['./reminders-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('fadeIn', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('200ms ease-out', style({ opacity: 1 }))
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ opacity: 0 }))
            ])
        ]),
        trigger('slideDown', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(-10px)' }),
                animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(-10px)' }))
            ])
        ]),
        trigger('slideIn', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateX(-10px)' }),
                animate('200ms ease-out', style({ opacity: 1, transform: 'translateX(0)' }))
            ])
        ])
    ],
    standalone: false
})
export class RemindersPanelComponent implements OnInit, OnDestroy, OnChanges {
  @Input() isOpen = false;
  @Output() onClose = new EventEmitter<void>();

  filteredReminders$: Observable<Reminder[]>;
  stats$: Observable<ReminderStats>;
  loading$: Observable<boolean>;

  @ViewChild('statusDropdownBtn', { read: ElementRef }) statusDropdownBtnRef!: ElementRef;

  snoozeOpenId: string | number | null = null;
  showCompleted = false;
  filterType: ReminderType | 'all' = 'all';
  filterStatus: 'pending' | 'all' | 'snoozed' | 'completed' | 'overdue' = 'pending';
  statusDropdownOpen = false;
  statusDropdownPosition = { top: 0, left: 0 };

  // Status filter options
  statusOptions: Array<{ value: 'pending' | 'all' | 'snoozed' | 'completed' | 'overdue'; label: string }> = [
    { value: 'pending', label: 'Pending' },
    { value: 'all', label: 'All' },
    { value: 'snoozed', label: 'Snoozed' },
    { value: 'completed', label: 'Completed' },
    { value: 'overdue', label: 'Overdue' }
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private remindersService: RemindersService,
    private dialog: MatDialog
  ) {
    this.filteredReminders$ = this.remindersService.filteredReminders$;
    this.stats$ = this.remindersService.stats$;
    this.loading$ = this.remindersService.loading$;
  }

  ngOnInit(): void {
    // Initialize filter
    this.remindersService.setFilter({ type: 'all', status: 'pending' });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When panel opens, fetch fresh data from API
    if (changes['isOpen'] && changes['isOpen'].currentValue === true) {
      this.remindersService.loadFromBackend();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onBackdropClick(): void {
    this.onClose.emit();
  }

  onFilterChange(type: ReminderType | 'all'): void {
    this.filterType = type;
    this.remindersService.setFilter({ type });
    // Reload from API when filter changes
    this.remindersService.loadFromBackend();
  }

  onStatusChange(status: 'pending' | 'all' | 'snoozed' | 'completed' | 'overdue'): void {
    this.filterStatus = status;
    this.showCompleted = status === 'completed' || status === 'all';
    this.remindersService.setFilter({ status });
    this.closeStatusDropdown();
    // Reload from API when status changes
    this.remindersService.loadFromBackend();
  }

  toggleStatusDropdown(): void {
    if (!this.statusDropdownOpen && this.statusDropdownBtnRef) {
      // Calculate dropdown position when opening
      const btnRect = this.statusDropdownBtnRef.nativeElement.getBoundingClientRect();
      this.statusDropdownPosition = {
        top: btnRect.bottom + 4, // 4px below button
        left: btnRect.left
      };
    }
    this.statusDropdownOpen = !this.statusDropdownOpen;
  }

  closeStatusDropdown(): void {
    this.statusDropdownOpen = false;
  }

  getCurrentStatusLabel(): string {
    const option = this.statusOptions.find(opt => opt.value === this.filterStatus);
    return option ? option.label : 'Pending';
  }

  onToggleCompleted(): void {
    this.showCompleted = !this.showCompleted;
    this.remindersService.setFilter({
      status: this.showCompleted ? 'all' : 'pending'
    });
  }

  onSnooze(event: { id: string | number; duration: number }): void {
    this.remindersService.snoozeReminder(event.id, event.duration)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.snoozeOpenId = null;
        },
        error: (err) => console.error('Snooze failed:', err)
      });
  }

  onDismiss(id: string | number): void {
    this.remindersService.deleteReminder(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (err) => console.error('Delete failed:', err)
      });
  }

  onComplete(id: string | number): void {
    this.remindersService.toggleCompleted(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (err) => console.error('Toggle failed:', err)
      });
  }

  onDismissAll(): void {
    this.remindersService.dismissAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => console.log(`Dismissed ${result.count} reminders`),
        error: (err) => console.error('Dismiss all failed:', err)
      });
  }

  onAddReminder(): void {
    const dialogRef = this.dialog.open(AddReminderModalComponent, {
      panelClass: 'reminder-modal-panel',
      disableClose: false,
      autoFocus: true
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result) {
          // Reminder was added
        }
      });
  }

  onEditReminder(reminder: Reminder): void {
    const dialogRef = this.dialog.open(AddReminderModalComponent, {
      panelClass: 'reminder-modal-panel',
      disableClose: false,
      autoFocus: true,
      data: { reminder }
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result) {
          // Reminder was updated
        }
      });
  }

  onSnoozeToggle(id: string | number | null): void {
    this.snoozeOpenId = id;
  }

  trackByReminderId(index: number, reminder: Reminder): string | number {
    return reminder.id;
  }

  getTypeIcon(type: ReminderType): string {
    return getTypeIcon(type);
  }

  getTypeColor(type: ReminderType): string {
    return getTypeColor(type);
  }

  getTypeBgColor(type: ReminderType): string {
    return getTypeBgColor(type);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    // Close status dropdown if clicked outside
    const clickedInsideStatus = target.closest('.reminders-status-dropdown-wrapper');
    if (!clickedInsideStatus && this.statusDropdownOpen) {
      this.closeStatusDropdown();
    }

    // Close snooze dropdown if clicked outside
    const clickedInsideSnooze = target.closest('.snooze-wrapper') || target.closest('.snooze-dropdown-overlay');
    if (!clickedInsideSnooze && this.snoozeOpenId !== null) {
      this.snoozeOpenId = null;
    }
  }
}
